import { useMemo, useState, useCallback } from 'react'
import { useBasketItems } from '@src/hooks/useBasketItems/useBasketItems'
import { UseBasketTotalsResponseData } from '@src/hooks/useTotals/useBasketTotals'
import { AddOnMenuGroups } from '@src/components/CheckoutModal/Checkout/AddOnItems/queries/__generated__/AddOnMenuGroups'
import { AsyncDataWrapper } from '@src/hooks/types'

/**
 * Menu group with computed state for checkout UI logic
 */
type GroupState = AddOnMenuGroups['menuItemGroupsForOutlet'][0] & {
  isActive: boolean // Basket total meets minimum requirement for group
  isAtMaxQuantity: boolean // Group has reached max quantity limit
  hasItemsInBasket: boolean // Any items from this group in basket
  currentQuantity: number // Total quantity from this group in basket
}

/**
 * Manages add-on menu groups in outlet menu and checkout flow - determines availability, tracks quantities, handles navigation.
 *
 * @param qualifyingMenuGroups From AddOnMenuGroups query - groups that meet basket/fulfillment criteria
 * @param basketTotals From useBasketTotals - contains nonAddOnBasketItemsTotal for threshold checks
 */
export const useAddOnMenuGroups = (
  qualifyingMenuGroups: AddOnMenuGroups['menuItemGroupsForOutlet'],
  basketTotals: AsyncDataWrapper<UseBasketTotalsResponseData>
) => {
  const { addOnItems } = useBasketItems()
  const [currentGroupId, setCurrentGroupId] = useState<string | null>(null)

  const groupStatesById = useMemo((): Record<string, GroupState> => {
    if (!qualifyingMenuGroups?.length) return {}

    return qualifyingMenuGroups.reduce((acc, group) => {
      const groupItemIds = group.outletMenuItems.map(item => item.menuItemId)
      const totalQuantityInBasket = addOnItems
        .filter(item => groupItemIds.includes(item.menuItemId))
        .reduce((sum, item) => sum + item.quantity, 0)

      const basketTotal =
        basketTotals.data?.checkoutBasketTotals?.nonAddOnBasketItemsTotal || 0
      const isActive =
        (group.addOnItemsMenuMinimumBasketTotal || 0) <= basketTotal
      const isAtMaxQuantity = group.addOnItemsMaxQuantity
        ? totalQuantityInBasket >= group.addOnItemsMaxQuantity
        : false
      const hasItemsInBasket = totalQuantityInBasket > 0

      acc[group.id] = {
        ...group,
        isActive,
        isAtMaxQuantity,
        hasItemsInBasket,
        currentQuantity: totalQuantityInBasket,
      }
      return acc
    }, {} as Record<string, GroupState>)
  }, [qualifyingMenuGroups, addOnItems, basketTotals.data])

  const activeGroupIds = useMemo(
    () =>
      Object.keys(groupStatesById).filter(
        id =>
          groupStatesById?.[id]?.isActive &&
          groupStatesById?.[id]?.outletMenuItems?.length
      ),
    [groupStatesById]
  )

  const effectiveCurrentGroupId = currentGroupId || activeGroupIds[0] || null

  const currentGroup = effectiveCurrentGroupId
    ? groupStatesById[effectiveCurrentGroupId]
    : null

  const currentGroupIndex = activeGroupIds.indexOf(
    effectiveCurrentGroupId || ''
  )
  const hasMoreGroups =
    activeGroupIds.length > 1 && currentGroupIndex < activeGroupIds.length - 1

  const nextGroup = useCallback(() => {
    if (hasMoreGroups) {
      const nextIndex = currentGroupIndex + 1
      setCurrentGroupId(activeGroupIds?.[nextIndex] || '')
    }
  }, [hasMoreGroups, currentGroupIndex, activeGroupIds])

  // Feature flag: restrict to highest qualifying group only vs allow all qualifying groups
  const usingHighestQualifyingFlag = useMemo(
    () =>
      qualifyingMenuGroups?.[0]?.outletMenuItems?.[0]?.outlet?.restaurant
        ?.addOnItemsUseHighestQualifyingMenuGroup || false,
    [qualifyingMenuGroups]
  )

  // Detects basket items from different group when highest-qualifying-only flag is active
  const conflictingGroup: string | null = useMemo(() => {
    if (!usingHighestQualifyingFlag) return null

    const currentMenuGroupId = effectiveCurrentGroupId?.split(':')[1]

    if (!currentMenuGroupId) return null

    return addOnItems.some(item =>
      item.parentMenuId?.includes(currentMenuGroupId)
    )
      ? null
      : addOnItems[0]?.parentMenuName || null
  }, [usingHighestQualifyingFlag, addOnItems, effectiveCurrentGroupId])

  return {
    groupStatesById, // All groups with computed state (active, quantities, etc)
    activeGroupIds, // IDs of groups meeting basket minimum requirements
    currentGroup, // Active group being displayed (used for name, description, items)
    currentGroupId: effectiveCurrentGroupId, // ID of current group
    hasMoreGroups, // Controls "Next" vs "Continue"/"No Thanks" button text
    nextGroup, // Advances to next group or navigates to payment
    totalActiveGroups: activeGroupIds.length, // Used with hasMoreGroups for navigation logic
    conflictingGroup, // Group name for conflict alert (highest-qualifying flag only)
  }
}
